import schema from '../../schema/editorSchema'
import { calculateImageDeliveryValues } from '../image/calculateImageDeliveryValues'
import { getNewQuestionTemplateAddMedia } from '../getNewQuestionTemplate'
import { generateRandQuestionId } from '../../../utils/generateRandQuestionId'
import { uploadNewImage } from '../../../actions/mediaUpload'
import { showNotification } from '../../../actions/notifications'
import { getActiveQuestionChoiceCount } from '../getActiveQuestionChoiceCount'
import { getActiveQuestionSlideWidth } from '../getActiveQuestionSlideWidth'
import { SetAttrsStep } from '../SetAttrsStep'
import store from '../../../store'
import analytics from '../../../utils/cdpAnalytics'
import { getImageUrl } from '../../../utils/mediaDeliveryUrls'
import { removeFailedMediaUploadNode } from './removeFailedMediaUploadNode'

// TODO cleanup and maybe combine prompt/choice image uploads

const defaultRotate = {
  angle: 0,
  flipH: false,
  flipV: false,
}
const defaultColorAdjust = {
  brightness: 0,
  saturation: 0,
  contrast: 0,
}

function checkIfFileIsAnimated(mediaUploadResponse) { // animated GIF check
  let isAnimated = false
  if (mediaUploadResponse.pages && mediaUploadResponse.pages > 1) {
    isAnimated = true
  }
  return isAnimated
}

function sendInsertImageAnalytics(node, imageAttrs, isImageSearch, isCopyPaste, isSearchBarPaste) {
  let isPromptMedia = false
  if (node.type.name === 'questionImage') {
    isPromptMedia = true
  }
  let copyPasteType
  if (isCopyPaste) {
    if (isSearchBarPaste) {
      copyPasteType = 'searchBar'
    } else {
      copyPasteType = 'questionBody'
    }
  }
  analytics.track('Insert Image', {
    isPromptMedia,
    deliveryUrl: imageAttrs.deliveryUrl,
    isImageSearch,
    isCopyPaste,
    copyPasteType,
  })
}

export function updateImageNodeWithSavedMediaUploadValues(nodeId) { // used for undo/redo stuff to avoid getting stuck in placeholder state
  const state = store.getState()
  if (state.imageUploads[nodeId]) { // check if have a saved upload response
    updateImageNodeWithMediaUploadValues(state.imageUploads[nodeId], nodeId)
  }
}

function updateImageNodeWithMediaUploadValues(mediaUploadResponse, nodeId, isImageSearch, isCopyPaste, isSearchBarPaste) {
  const fileId = mediaUploadResponse.public_id
  const intrinsicHeight = mediaUploadResponse.height
  const intrinsicWidth = mediaUploadResponse.width
  const isAnimated = checkIfFileIsAnimated(mediaUploadResponse)

  if (isAnimated) {
    const fullSizeStaticImageUrl = getImageUrl(fileId, { transformations: 'pg_1', format: 'png' })
    return store.dispatch(beginImageUpload(fullSizeStaticImageUrl)).then((response) => {
      // Show notification that has been converted to static image
      store.dispatch(showNotification("If you'd like to insert it as an animated GIF instead, go to Insert GIF.", 'GIF inserted as an image.', 'default'))
      updateImageNodeWithMediaUploadValues(response, nodeId)
    })
  }

  store.dispatch({ type: 'MEDIA_UPLOAD_SUCCESS', nodeId, mediaUploadResponse })
  const originalDimensions = {
    w: intrinsicWidth,
    h: intrinsicHeight,
  }

  const imageDeliveryValues = calculateImageDeliveryValues(fileId, originalDimensions, null, defaultRotate, defaultColorAdjust, [], [])
  const { deliveryUrl } = imageDeliveryValues

  const { tr } = window.view.state
  let imageNode
  let imageNodePos
  window.view.state.doc.nodesBetween(0, window.view.state.doc.content.size, (node, pos) => {
    if (node.attrs.nodeId && node.attrs.nodeId === nodeId) {
      imageNodePos = pos
      imageNode = node
    }
  })
  if (imageNode) {
    const imageAttrs = { ...imageNode.attrs }
    imageAttrs.originalDimensions = originalDimensions
    imageAttrs.deliveryDimensions = {
      w: imageDeliveryValues.w,
      h: imageDeliveryValues.h,
    }
    imageAttrs.fileId = fileId
    imageAttrs.deliveryUrl = deliveryUrl
    sendInsertImageAnalytics(imageNode, imageAttrs, isImageSearch, isCopyPaste, isSearchBarPaste)
    tr.step(new SetAttrsStep(imageNodePos, imageAttrs))
    tr.setMeta('addToHistory', false)
    tr.setMeta('triggerParseDoc', true)
    window.view.dispatch(tr)
  }
}

// the browser could not decode the file, so no placeholder is inserted and nothing is uploaded
function showUnreadableImageNotification() {
  store.dispatch(showNotification(" we're unable to read this image file. Please try a different image.", 'Image upload failed:', 'destroy'))
}

function beginImageUpload(src) {
  return (dispatch) => dispatch(uploadNewImage(src)).then((response) => response)
    .catch((error) => {
      if (error.response && error.response.status === 400) {
        store.dispatch(showNotification(" we're unable to insert the image. Please try a different image.", 'Image upload failed:', 'destroy'))
      }
      throw (error)
    })
}

function addQuestionImage(src, thumbnail, title, attribution, insertPos, questionPos, questionNode, file, isImageSearch, isCopyPaste, isSearchBarPaste) {
  return (dispatch) => {
    const { tr } = window.view.state
    const newTemplate = getNewQuestionTemplateAddMedia(questionNode.attrs.template)
    tr.setNodeMarkup(questionPos, null, { ...questionNode.attrs, template: newTemplate })
    window.view.state.doc.nodesBetween(questionPos, questionPos + questionNode.nodeSize, (node, pos) => {
      if (node.type.name === 'choiceImage' || node.type.name === 'choiceVideo' || node.type.name === 'choiceSound' || node.type.name === 'choice') {
        tr.setNodeMarkup(pos, null, { ...node.attrs, template: newTemplate })
      }
    })
    const nodeId = generateRandQuestionId()
    let placeholderImage = ''
    let placeholderDimensions = {
      w: '1',
      h: '1',
    }
    if (thumbnail && thumbnail.src) {
      placeholderImage = thumbnail.src
      placeholderDimensions = {
        w: thumbnail.w,
        h: thumbnail.h,
      }
    }

    const type = schema.nodes.questionImage
    const nowDate = new Date()

    const imageAttrs = {
      nodeId,
      addedAt: nowDate,
      placeholderImage,
      fileId: '',
      title,
      attribution,
      deliveryUrl: '',
      template: newTemplate,
      originalDimensions: {
        w: '',
        h: '',
      },
      deliveryDimensions: {
        w: '',
        h: '',
      },
      placeholderDimensions,
    }

    imageAttrs.questionMediaDynamicHeight = questionNode.attrs.questionMediaDynamicHeight
    imageAttrs.slideWidth = getActiveQuestionSlideWidth()

    if (file) {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = (event) => {
        imageAttrs.placeholderImage = event.target.result
        const img = new Image()
        img.src = event.target.result
        img.onerror = showUnreadableImageNotification
        img.onload = function () {
          const placeholderDimensions = {
            w: img.naturalWidth,
            h: img.naturalHeight,
          }
          imageAttrs.placeholderDimensions = placeholderDimensions
          const imageNode = type.create(imageAttrs)
          tr.replaceWith(insertPos.start, insertPos.end, imageNode)
          tr.setMeta('triggerParseDoc', true)
          window.view.dispatch(tr)
          return dispatch(beginImageUpload(event.target.result)).then((response) => {
            updateImageNodeWithMediaUploadValues(response, nodeId, isImageSearch, isCopyPaste, isSearchBarPaste)
          })
            .catch(() => removeFailedMediaUploadNode(nodeId))
        }
      }
    } else {
      const imageNode = type.create(imageAttrs)
      tr.replaceWith(insertPos.start, insertPos.end, imageNode)
      tr.setMeta('triggerParseDoc', true)
      window.view.dispatch(tr)
      return dispatch(beginImageUpload(src)).then((response) => {
        updateImageNodeWithMediaUploadValues(response, nodeId, isImageSearch, isCopyPaste, isSearchBarPaste)
      })
        .catch(() => removeFailedMediaUploadNode(nodeId))
    }
  }
}

function addChoiceImage(src, thumbnail, title, attribution, insertPos, file, isImageSearch, isCopyPaste, isSearchBarPaste) {
  return (dispatch) => {
    const { tr } = window.view.state
    const type = schema.nodes.choiceImage
    const nodeId = generateRandQuestionId()
    let placeholderImage = ''
    let placeholderDimensions = {
      w: '1',
      h: '1',
    }
    if (thumbnail && thumbnail.src) {
      placeholderImage = thumbnail.src
      placeholderDimensions = {
        w: thumbnail.w,
        h: thumbnail.h,
      }
    }
    const nowDate = new Date()

    let imageFill = true
    const savedDefaultChoiceImageFill = JSON.parse(localStorage.getItem('defaultChoiceImageFill'))
    if (savedDefaultChoiceImageFill || savedDefaultChoiceImageFill === false) {
      imageFill = savedDefaultChoiceImageFill
    }

    const imageAttrs = {
      nodeId,
      addedAt: nowDate,
      placeholderImage,
      questionChoiceCount: getActiveQuestionChoiceCount(),
      slideWidth: getActiveQuestionSlideWidth(),
      fileId: '',
      title,
      attribution,
      deliveryUrl: '',
      placeholderDimensions,
      imageFill,
    }

    if (file) {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = (event) => {
        imageAttrs.placeholderImage = event.target.result
        const img = new Image()
        img.src = event.target.result
        img.onerror = showUnreadableImageNotification
        img.onload = function () {
          const imageNode = type.create(imageAttrs)
          tr.replaceWith(insertPos.start, insertPos.end, imageNode)
          tr.setMeta('triggerParseDoc', true)
          window.view.dispatch(tr)
          return dispatch(beginImageUpload(event.target.result)).then((response) => {
            // const isUpload=true
            updateImageNodeWithMediaUploadValues(response, nodeId, isImageSearch, isCopyPaste, isSearchBarPaste)
          })
            .catch(() => removeFailedMediaUploadNode(nodeId))
        }
      }
    } else {
      const imageNode = type.create(imageAttrs)
      tr.replaceWith(insertPos.start, insertPos.end, imageNode)
      tr.setMeta('triggerParseDoc', true)
      window.view.dispatch(tr)
      return dispatch(beginImageUpload(src)).then((response) => {
        updateImageNodeWithMediaUploadValues(response, nodeId, isImageSearch, isCopyPaste, isSearchBarPaste)
      })
        .catch(() => removeFailedMediaUploadNode(nodeId))
    }
  }
}

export { addQuestionImage, addChoiceImage }
